'use client';

import type { CompanyCountryCode } from '@/lib/countries';
import { memo, useEffect, useMemo, useState } from 'react';
import { Building2, Lock, User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CompanyHistoryEvent, GesellschafterItem, RegisterParticipantItem, SubsidiaryCompanyItem } from '@/lib/backend-api';
import { NON_MANAGER_ROLE_CODES, isOwnerRole } from '@/lib/company-utils';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SourceAttribution } from './SourceAttribution';
import { GesellschafterTabContent, buildKommanditistenRows } from './GesellschafterTabContent';
import { ManagersTabContent } from './ManagersTabContent';
import { SubsidiariesTabContent } from './SubsidiariesTabContent';

/**
 * Tabbed card showing company structure: Managers, Beteiligungen (subsidiaries), Gesellschafter.
 *
 * Tabs are conditionally rendered based on data availability; the active tab is kept in sync
 * as async data loads (so a guest who initially only sees "Managers" smoothly switches when
 * subsidiaries arrive). Guests also see a locked Gesellschafter tab as a sign-in prompt when
 * no shareholder data is present.
 *
 * Delegates per-tab rendering to {@link ManagersTabContent}, {@link GesellschafterTabContent}
 * and {@link SubsidiariesTabContent}.
 */
export const PeopleTabbedCard = memo(function PeopleTabbedCard({
  participants,
  gesellschafter,
  hasGesellschafterliste,
  kommanditisten = [],
  showKommanditPercent = true,
  subsidiaries,
  isAuthenticated,
  euId,
  showRegisterAttribution = false,
  country,
  registerCourt,
  registerId,
  capturedAt,
  className,
}: {
  participants: RegisterParticipantItem[];
  gesellschafter: GesellschafterItem[];
  hasGesellschafterliste: boolean;
  kommanditisten?: CompanyHistoryEvent[];
  showKommanditPercent?: boolean;
  subsidiaries: SubsidiaryCompanyItem[];
  isAuthenticated: boolean;
  euId: string;
  showRegisterAttribution?: boolean;
  /** Picks the register credited by the attribution: the Firmenbuch for AT,
   *  else the German Handelsregister (callers hide it for CH). */
  country?: CompanyCountryCode;
  registerCourt?: string | null;
  registerId?: string | null;
  capturedAt?: string | null;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const { t } = useTranslation();

  const managers = useMemo(
    () =>
      participants.filter(
        (p) =>
          (!p.role_code || !NON_MANAGER_ROLE_CODES.has(p.role_code)) &&
          !isOwnerRole(p.role ?? ''),
      ),
    [participants],
  );

  // Participants with owner roles (Kommanditist etc.) that aren't in Gesellschafterliste
  const ownerParticipants = useMemo(
    () => participants.filter((p) => isOwnerRole(p.role ?? '')),
    [participants],
  );

  // CD Kommanditisten that will render as their own owner rows (e.g. a large
  // standalone KG whose limited partners are CD-only, not in the SI). Computed
  // here too so tab visibility + count match what the table actually shows.
  const kommanditistenRows = useMemo(
    () =>
      buildKommanditistenRows(
        gesellschafter,
        ownerParticipants,
        kommanditisten,
        showKommanditPercent,
      ),
    [gesellschafter, ownerParticipants, kommanditisten, showKommanditPercent],
  );
  const ownerRowCount = ownerParticipants.length + kommanditistenRows.length;

  const hasManagers = managers.length > 0;
  const hasGesellschafter =
    (hasGesellschafterliste && gesellschafter.length > 0) || ownerRowCount > 0;
  const hasSubsidiaries = subsidiaries.length > 0;

  const firstAvailableTab = hasManagers
    ? 'managers'
    : hasSubsidiaries
      ? 'subsidiaries'
      : hasGesellschafter
        ? 'gesellschafter'
        : 'managers';

  const [activeTab, setActiveTab] = useState(firstAvailableTab);

  // Keep activeTab in sync when available tabs change (e.g. data loads async)
  useEffect(() => {
    const availableTabs = [
      hasManagers && 'managers',
      hasSubsidiaries && 'subsidiaries',
      hasGesellschafter && 'gesellschafter',
    ].filter(Boolean) as string[];
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]); // eslint-disable-line react-hooks/set-state-in-effect -- falls back to the first tab that has data once data loads
    }
  }, [hasManagers, hasGesellschafter, hasSubsidiaries, activeTab]);

  // Show the card when any tab has content, or when guest should see the locked Gesellschafter tab
  const showLockedGesellschafter = !isAuthenticated && !hasGesellschafter;
  if (
    !hasManagers &&
    !hasGesellschafter &&
    !hasSubsidiaries &&
    !showLockedGesellschafter
  ) {
    return null;
  }

  const totalCount =
    managers.length +
    gesellschafter.length +
    ownerRowCount +
    subsidiaries.length;

  return (
    <Card variant="grid" className={className}>
      <CardHeader>
        <div className="flex items-start gap-2 w-full">
          <div className="flex-1">
            <CardTitle as="h2">
              {t('companyDetail.companyStructure', 'Unternehmensstruktur')}
            </CardTitle>
          </div>
          <Badge variant="outline" className="shrink-0 mt-1">
            {totalCount}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Scrollable on narrow screens so no tab label ("Gesellschafter")
              gets truncated; the scrollbar itself is hidden. */}
          <div className="px-5 pt-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList
              variant="line"
              size="sm"
              className={cn(
                'w-max min-w-full',
                !isAuthenticated ? 'mb-1' : 'mb-3',
              )}
            >
              {hasManagers && (
                <TabsTrigger
                  value="managers"
                  className="gap-1 text-[10px] sm:text-xs justify-start shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0"
                >
                  <Users size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyDetail.managers')}
                  </span>
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {managers.length}
                  </Badge>
                </TabsTrigger>
              )}
              {hasSubsidiaries && (
                <TabsTrigger
                  value="subsidiaries"
                  className="gap-1 text-[10px] sm:text-xs justify-start shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0"
                >
                  <Building2 size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyDetail.beteiligungen')}
                  </span>
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {subsidiaries.length}
                  </Badge>
                </TabsTrigger>
              )}
              {hasGesellschafter ? (
                <TabsTrigger
                  value="gesellschafter"
                  className="gap-1 text-[10px] sm:text-xs justify-start shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0"
                >
                  <User size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyDetail.gesellschafter')}
                  </span>
                  {gesellschafter.length + ownerRowCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                    >
                      {gesellschafter.length + ownerRowCount}
                    </Badge>
                  )}
                </TabsTrigger>
              ) : showLockedGesellschafter ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground cursor-not-allowed px-2 py-1.5 shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0">
                        <Lock size={12} className="shrink-0" />
                        <span className="truncate">
                          {t('companyDetail.gesellschafter')}
                        </span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('companyDetail.loginToSeeGesellschafter')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
            </TabsList>
          </div>

          {/* forceMount keeps every panel in the SSR HTML so the company
              links inside (Gesellschafter / Beteiligungen) are crawlable
              even when another tab is active. Radix doesn't hide
              force-mounted panels itself, so inactive ones are hidden via
              data-[state=inactive]:hidden. */}
          {hasManagers && (
            <TabsContent
              value="managers"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <ManagersTabContent
                managers={managers}
                isAuthenticated={isAuthenticated}
              />
            </TabsContent>
          )}
          {hasGesellschafter && (
            <TabsContent
              value="gesellschafter"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <GesellschafterTabContent
                gesellschafter={gesellschafter}
                ownerParticipants={ownerParticipants}
                kommanditisten={kommanditisten}
                showKommanditPercent={showKommanditPercent}
                isAuthenticated={isAuthenticated}
                euId={euId}
              />
            </TabsContent>
          )}
          {hasSubsidiaries && (
            <TabsContent
              value="subsidiaries"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <SubsidiariesTabContent
                subsidiaries={subsidiaries}
                isAuthenticated={isAuthenticated}
                euId={euId}
              />
            </TabsContent>
          )}
        </Tabs>
        {showRegisterAttribution && (
          <SourceAttribution
            source={country === 'AT' ? 'firmenbuch' : 'handelsregister'}
            registerCourt={registerCourt}
            registerId={registerId}
            capturedAt={capturedAt}
            className="mx-5"
          />
        )}
      </CardContent>
    </Card>
  );
});
